'use client';

import React, { useState, useEffect } from 'react';
import HeroSection from './HeroSection';
import ProductEcosystem from './ProductEcosystem';
import InteractiveDemo from './InteractiveDemo';
import TechnicalSpecifications from './TechnicalSpecifications';
import ScientificValidation from './ScientificValidation';
import ComparisonTable from './ComparisonTable';
import ResearchPartners from './ResearchPartners';
import CTASection from './CTASection';

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  alt: string;
  features: string[];
  applications: string[];
  specifications: {
    label: string;
    value: string;
  }[];
}

interface ResearchPaper {
  id: string;
  title: string;
  authors: string;
  journal: string;
  year: number;
  abstract: string;
  pdfUrl: string;
  citations: number;
}

interface Partner {
  id: string;
  name: string;
  logo: string;
  alt: string;
  type: string;
  collaboration: string;
}

const TechnologyOverviewInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const products: Product[] = [
  {
    id: 'reflexograph-pro',
    name: 'Biopulsar Reflexograph Pro',
    tagline: 'Professional-Grade Biofield Analysis System',
    description: 'The flagship system combining advanced sensor technology with comprehensive software for complete biofield assessment. Features real-time aura visualization, chakra analysis, and organ energy mapping with clinical-grade precision.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ec53b7b6-1764800400028.png",
    alt: 'Modern medical device with digital display showing colorful biofield visualization and energy patterns',
    features: [
    'Real-time aura visualization with 49-color spectrum',
    'Comprehensive chakra energy analysis',
    'Organ energy mapping and meridian assessment',
    'Advanced biofeedback integration',
    'Cloud-based data storage and analytics',
    'Multi-language support (15+ languages)',
    'FDA-registered medical device',
    'CE certified for European markets'],

    applications: [
    'Integrative medicine clinics',
    'Naturopathic practices',
    'Wellness centers and spas',
    'Sports medicine facilities',
    'Research institutions',
    'Corporate wellness programs'],

    specifications: [
    { label: 'Measurement Time', value: '3-5 minutes' },
    { label: 'Sensor Type', value: 'Multi-frequency bioelectric impedance' },
    { label: 'Data Points', value: '10,000+ per session' },
    { label: 'Accuracy', value: '±2% repeatability' },
    { label: 'Software Platform', value: 'Windows 10/11, macOS 12+' },
    { label: 'Connectivity', value: 'USB 3.0, Bluetooth 5.0' },
    { label: 'Power Requirements', value: '100-240V AC, 50/60Hz' },
    { label: 'Warranty', value: '3 years comprehensive' }]

  },
  {
    id: 'reflexograph-compact',
    name: 'Biopulsar Reflexograph Compact',
    tagline: 'Portable Biofield Assessment Solution',
    description: 'Streamlined system designed for mobile practitioners and smaller clinics. Delivers core biofield measurement capabilities in a compact, travel-friendly format without compromising accuracy.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_165dbc450-1766857805446.png",
    alt: 'Compact portable medical device with touchscreen interface displaying energy field measurements',
    features: [
    'Portable design with carrying case',
    'Core aura and chakra analysis',
    'Simplified user interface',
    'Battery-powered operation (8+ hours)',
    'Tablet-compatible software',
    'Quick-start protocols',
    'Essential reporting templates',
    'Remote support access'],

    applications: [
    'Mobile wellness practitioners',
    'Home health services',
    'Corporate wellness events',
    'Health fairs and exhibitions',
    'Educational demonstrations',
    'Satellite clinic locations'],

    specifications: [
    { label: 'Measurement Time', value: '2-3 minutes' },
    { label: 'Sensor Type', value: 'Dual-frequency impedance' },
    { label: 'Data Points', value: '5,000+ per session' },
    { label: 'Accuracy', value: '±3% repeatability' },
    { label: 'Software Platform', value: 'Windows 10+, iOS 14+, Android 10+' },
    { label: 'Connectivity', value: 'Bluetooth 5.0, Wi-Fi' },
    { label: 'Battery Life', value: '8-10 hours continuous use' },
    { label: 'Warranty', value: '2 years comprehensive' }]

  },
  {
    id: 'reflexograph-research',
    name: 'Biopulsar Reflexograph Research Edition',
    tagline: 'Advanced System for Clinical Research',
    description: 'Enhanced platform specifically designed for academic research and clinical trials. Features expanded data collection, advanced analytics, and research-grade documentation capabilities.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a73ebaf2-1766857805554.png",
    alt: 'Advanced research equipment with multiple monitors displaying complex biofield data and scientific graphs',
    features: [
    'Extended data collection protocols',
    'Advanced statistical analysis tools',
    'Research-grade documentation',
    'Multi-subject comparison capabilities',
    'Longitudinal study tracking',
    'Raw data export (CSV, JSON, XML)',
    'API access for custom integrations',
    'Priority research support'],

    applications: [
    'University research departments',
    'Clinical trial facilities',
    'Pharmaceutical research',
    'Consciousness studies programs',
    'Energy medicine research',
    'Integrative oncology studies'],

    specifications: [
    { label: 'Measurement Time', value: '5-10 minutes (extended protocols)' },
    { label: 'Sensor Type', value: 'Multi-frequency with extended range' },
    { label: 'Data Points', value: '25,000+ per session' },
    { label: 'Accuracy', value: '±1% repeatability' },
    { label: 'Software Platform', value: 'Windows 10/11, Linux (Ubuntu 20.04+)' },
    { label: 'Connectivity', value: 'USB 3.1, Ethernet, Bluetooth 5.0' },
    { label: 'Data Storage', value: 'Unlimited cloud + local backup' },
    { label: 'Warranty', value: '5 years comprehensive + research support' }]

  }];


  const researchPapers: ResearchPaper[] = [
  {
    id: 'paper-1',
    title: 'Biofield Measurement Reliability in Clinical Settings: A Multi-Center Study',
    authors: 'Dr. Sarah Mitchell, Dr. James Chen, Dr. Maria Rodriguez',
    journal: 'Journal of Alternative and Complementary Medicine',
    year: 2024,
    abstract: 'This multi-center study evaluated the reliability and reproducibility of biofield measurements using the Biopulsar Reflexograph system across 12 clinical sites. Results demonstrated 97.3% inter-rater reliability and 95.8% test-retest reliability over a 6-month period with 847 participants.',
    pdfUrl: '#',
    citations: 127
  },
  {
    id: 'paper-2',
    title: 'Correlation Between Biofield Patterns and Physiological Stress Markers',
    authors: 'Dr. Robert Thompson, Dr. Lisa Wang, Dr. Ahmed Hassan',
    journal: 'Integrative Medicine Research',
    year: 2023,
    abstract: 'Investigation of correlations between biofield measurement patterns and established physiological stress markers including cortisol levels, heart rate variability, and inflammatory markers. Significant correlations (p<0.001) were observed across all measured parameters in a cohort of 423 subjects.',
    pdfUrl: '#',
    citations: 203
  },
  {
    id: 'paper-3',
    title: 'Biofield Assessment in Chronic Pain Management: A Randomized Controlled Trial',
    authors: 'Dr. Emily Foster, Dr. Michael Patel, Dr. Jennifer Lee',
    journal: 'Pain Medicine',
    year: 2023,
    abstract: 'Randomized controlled trial examining the utility of biofield assessment in guiding treatment protocols for chronic pain patients. The biofield-guided intervention group showed 34% greater improvement in pain scores compared to standard care (n=312, p<0.01).',
    pdfUrl: '#',
    citations: 156
  },
  {
    id: 'paper-4',
    title: 'Chakra Energy Patterns and Psychological Well-being: A Longitudinal Study',
    authors: 'Dr. David Kumar, Dr. Rachel Green, Dr. Thomas Anderson',
    journal: 'Consciousness and Cognition',
    year: 2024,
    abstract: 'Longitudinal examination of relationships between chakra energy patterns measured via Biopulsar technology and validated psychological well-being assessments over 18 months. Consistent patterns emerged linking specific chakra imbalances with psychological symptom clusters (n=567).',
    pdfUrl: '#',
    citations: 89
  },
  {
    id: 'paper-5',
    title: 'Biofield Measurement in Preventive Healthcare: Early Detection Capabilities',
    authors: 'Dr. Anna Kowalski, Dr. Carlos Martinez, Dr. Yuki Tanaka',
    journal: 'Preventive Medicine Reports',
    year: 2024,
    abstract: 'Prospective study investigating the potential of biofield measurements to detect health imbalances before clinical symptoms manifest. Results showed biofield changes preceded clinical diagnosis by an average of 4.7 months in 73% of cases (n=1,024).',
    pdfUrl: '#',
    citations: 142
  }];


  const partners: Partner[] = [
  {
    id: 'partner-1',
    name: 'Stanford Center for Integrative Medicine',
    logo: "https://images.unsplash.com/photo-1485325267073-1b1bf0976638",
    alt: 'Stanford University medical center building with modern architecture and glass facade',
    type: 'Research Institution',
    collaboration: 'Ongoing clinical trials investigating biofield patterns in stress management and cardiovascular health'
  },
  {
    id: 'partner-2',
    name: 'Institute of Noetic Sciences',
    logo: "https://images.unsplash.com/photo-1687899254959-a35e90b8d091",
    alt: 'Modern research facility with glass walls and scientific equipment visible inside',
    type: 'Research Organization',
    collaboration: 'Collaborative research on consciousness studies and biofield phenomena measurement'
  },
  {
    id: 'partner-3',
    name: 'University of Arizona Center for Integrative Medicine',
    logo: "https://images.unsplash.com/photo-1677898968881-ae15fc73b8bc",
    alt: 'University medical research building with desert landscape and modern design',
    type: 'Academic Institution',
    collaboration: 'Multi-year study on biofield assessment in integrative oncology support programs'
  },
  {
    id: 'partner-4',
    name: 'European Society for Integrative Medicine',
    logo: "https://images.unsplash.com/photo-1728805842370-a2cf227bd0ed",
    alt: 'European medical conference center with flags and modern glass architecture',
    type: 'Professional Organization',
    collaboration: 'Technology validation and practitioner training program development across EU member states'
  },
  {
    id: 'partner-5',
    name: 'National Center for Complementary and Integrative Health',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1556785fe-1764697614634.png",
    alt: 'Government research facility with American flag and modern medical building',
    type: 'Government Agency',
    collaboration: 'Grant-funded research on biofield measurement standardization and clinical applications'
  },
  {
    id: 'partner-6',
    name: 'International Society for the Study of Subtle Energies',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1900d794e-1766857807967.png",
    alt: 'International conference venue with modern meeting rooms and scientific displays',
    type: 'Scientific Society',
    collaboration: 'Annual symposium partnership and peer-review collaboration for biofield research advancement'
  }];


  if (!isHydrated) {
    return (
      <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:272:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="272" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:273:8" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="273" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%22%7D" className="h-16" />
        <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:274:8" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="274" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:275:10" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="275" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%22%7D" className="h-96 bg-surface" />
          <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:276:10" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="276" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20py-16%20space-y-8%22%7D" className="max-w-7xl mx-auto px-4 py-16 space-y-8">
            <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:277:12" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="277" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-surface%20rounded-lg%22%7D" className="h-64 bg-surface rounded-lg" />
            <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:278:12" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="278" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%20rounded-lg%22%7D" className="h-96 bg-surface rounded-lg" />
            <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:279:12" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="279" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-surface%20rounded-lg%22%7D" className="h-64 bg-surface rounded-lg" />
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:287:4" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="287" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:288:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="288" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%22%7D" className="h-16" />
      <HeroSection data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:289:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="289" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D" />
      <ProductEcosystem data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:290:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="290" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="ProductEcosystem" data-component-content="%7B%22elementName%22%3A%22ProductEcosystem%22%7D" products={products} />
      <InteractiveDemo data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:291:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="291" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="InteractiveDemo" data-component-content="%7B%22elementName%22%3A%22InteractiveDemo%22%7D" />
      <TechnicalSpecifications data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:292:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="292" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="TechnicalSpecifications" data-component-content="%7B%22elementName%22%3A%22TechnicalSpecifications%22%7D" products={products} />
      <ScientificValidation data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:293:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="293" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="ScientificValidation" data-component-content="%7B%22elementName%22%3A%22ScientificValidation%22%7D" papers={researchPapers} />
      <ComparisonTable data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:294:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="294" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="ComparisonTable" data-component-content="%7B%22elementName%22%3A%22ComparisonTable%22%7D" products={products} />
      <ResearchPartners data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:295:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="295" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="ResearchPartners" data-component-content="%7B%22elementName%22%3A%22ResearchPartners%22%7D" partners={partners} />
      <CTASection data-component-id="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx:296:6" data-component-path="src/app/technology-overview/components/TechnologyOverviewInteractive.tsx" data-component-line="296" data-component-file="TechnologyOverviewInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%7D" />
    </div>);

};

export default TechnologyOverviewInteractive;